'use client';

import { useState, useEffect } from 'react';
import Image from 'next/image';
import SafeLink from '../components/SafeLink';

interface NewsItem {
  id: string;
  type: string;
  date: string;
  title: string;
  description: string;
  image: string | null;
  link: string;
}

interface StrapiItem {
  id: number;
  attributes?: {
    type?: string;
    publication_date?: string;
    date?: string;
    title?: string;
    description?: string;
    image?: {
      data?: {
        attributes?: {
          url?: string;
        };
      };
    } | null;
  };
}

const STRAPI_URL = process.env.NEXT_PUBLIC_STRAPI_API_URL || '';

const transformStrapiData = (items: StrapiItem[] | undefined): NewsItem[] => {
  if (!Array.isArray(items) || items.length === 0) return [];

  return items.map((item) => {
    const source = item.attributes || {};
    const rawImage = source.image?.data?.attributes?.url || null;
    const imageUrl = rawImage
      ? rawImage.startsWith('http')
        ? rawImage
        : `${STRAPI_URL}${rawImage}`
      : null;


    return {
      id: String(item.id),
      type: source.type || 'Новость',
      date: source.publication_date || source.date || '',
      title: source.title || 'Без названия',
      description: source.description || '',
      image: imageUrl,
      link: `/news/${item.id}`,
    };
  });
};

export default function LatestDevelopmentSection() {
  const [newsItems, setNewsItems] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<string>('Все');

  useEffect(() => {
    async function fetchNews() {
      try {
        setLoading(true);
        setError(null);

        let url = `${process.env.NEXT_PUBLIC_STRAPI_API_URL}/api/posts?populate=image`;

        if (filterType !== 'Все') {
          url += `&filters[type][$eq]=${encodeURIComponent(filterType)}`;
        }


        const res = await fetch(url, { cache: 'no-store' });
        const data = await res.json();

        if (!res.ok) {
          throw new Error(data?.error?.message || `Ошибка загрузки (${res.status})`);
        }

        const transformed = transformStrapiData(data?.data);
        setNewsItems(transformed);
      } catch (e: any) {
        console.error('Failed to fetch news:', e);
        setError(e.message || 'Не удалось загрузить новости.');
        setNewsItems([]);
      } finally {
        setLoading(false);
      }
    }

    fetchNews();
  }, [filterType]);

  if (loading) {
    return (
      <section className="bg-white py-16 text-center">
        <p className="text-blue-900 text-xl font-bold">Загрузка новостей...</p>
      </section>
    );
  }

  if (error) {
    return (
      <section className="bg-white py-16 text-center">
        <p className="text-red-600 text-xl font-bold">Новости временно недоступны</p>
        <p className="text-gray-600">{error}</p>
      </section>
    );
  }

  if (!newsItems.length) {
    return (
      <section className="bg-white py-16 text-center">
        <p className="text-gray-600 text-lg">Нет новостей по выбранной категории.</p>
      </section>
    );
  }

  return (
    <section className="bg-white py-12 md:py-16">
      <div className="max-w-[1900px] mx-auto px-6 md:px-12">
        <h2 className="text-3xl md:text-4xl font-bold text-center text-black mb-8 md:mb-10">
          Наши последние разработки
        </h2>

        <div className="flex justify-center mb-8">
          <nav className="flex space-x-4 md:space-x-6 overflow-x-auto whitespace-nowrap pb-2">
            {['Все', 'Новость', 'Событие', 'Инсайт', 'Публикация'].map((type) => (
              <button
                key={type}
                onClick={() => setFilterType(type)}
                className={`text-sm md:text-xl font-bold transition-colors duration-300 ${filterType === type ? 'text-blue-900' : 'text-blue-900 hover:text-blue-500'
                  }`}
              >
                {type}
              </button>
            ))}
          </nav>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {newsItems.slice(0, 3).map((item) => (
            <SafeLink
              key={item.id}
              href={item.link}
              className="group bg-gray-100 shadow-lg overflow-hidden border border-gray-200 rounded-lg hover:shadow-xl transition duration-300 flex flex-col"
            >
              {item.image && (
                <div className="relative w-full aspect-[16/9] overflow-hidden">
                  <Image
                    src={item.image}
                    alt={item.title}
                    fill
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                    className="object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
              )}
              <div className="p-6 flex flex-col flex-grow">
                <span className="inline-block text-gray-500 text-sm font-semibold mb-2">
                  {item.type}
                </span>
                <div className="text-gray-500 text-sm mb-2">{item.date}</div>
                <h3 className="text-lg md:text-xl font-bold text-black mb-3 line-clamp-2">
                  {item.title}
                </h3>
                <p className="text-gray-700 mb-4 text-sm md:text-base line-clamp-3">
                  {item.description}
                </p>
                <a
                  href={item.link}
                  className="mt-auto w-fit font-bold bg-blue-900 text-white px-6 py-2 rounded-full hover:bg-blue-500 transition"
                >
                  Читать далее
                </a>
              </div>
            </SafeLink>
          ))}
        </div>

        <div className="text-center mt-8 md:mt-12">
          <SafeLink
            href="/news"
            className="inline-flex items-center text-blue-900 font-bold text-xl transition-colors duration-200 group hover:text-blue-500"
          >
            Больше
            <svg
              className="ml-2 w-3 h-3 transition-transform duration-200 group-hover:translate-x-1"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M19 14l-7 7m0 0l-7-7m7 7V3"
              ></path>
            </svg>
          </SafeLink>
        </div>
      </div>
    </section>
  );
}
